import { CalendarId } from '@wiztime/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNow } from '../../hooks/useNow';
import { useSettingsState } from '../../hooks/useSettings';
import { cx } from '../../lib/cx';
import { Card } from '../../ui/Card';
import { useCalendarModule } from './calendarModule';
import { countdownLabel, lunarLabelOf } from './format';
import { calendarDateOf, readableDates, upcomingEntries } from './model';
import { useImportantDates } from './useImportantDates';
import styles from './ImportantDates.module.css';

const PACK_LUNAR_PREFIX = 'cn-';

/**
 * Day view card "Upcoming" (docs/design/important-dates-ui.md §3): important dates inside their
 * largest lead day and holidays of the enabled packs inside the holiday window. Counts calendar
 * days of the device, not personal days. Without content (nothing created, no pack enabled, or
 * nothing in range) the card is not shown, and the calendar module is not loaded until something
 * could be shown.
 */
export function UpcomingCard() {
  const { t } = useTranslation();
  const now = useNow(60_000);
  const today = calendarDateOf(now);
  const dates = useImportantDates();
  const settings = useSettingsState();
  const records = dates.data;
  const readable = useMemo(() => (records === undefined ? [] : readableDates(records, true)), [records]);
  // The settings read returns a new array each time; the pack ids themselves decide when to recompute.
  const packsKey = (settings.data?.holidayPacks ?? []).join('|');
  const holidayPacks = useMemo(() => (packsKey === '' ? [] : packsKey.split('|')), [packsKey]);
  const holidayWindowDays = settings.data?.holidayWindowDays ?? 0;
  // The library is only worth loading when an entry could exist.
  const wanted = readable.length > 0 || holidayPacks.length > 0;
  const module = useCalendarModule(wanted);

  const entries = useMemo(
    () =>
      module.calendar === null
        ? []
        : upcomingEntries(module.calendar, readable, { holidayPacks, holidayWindowDays }, today),
    [module.calendar, readable, holidayPacks, holidayWindowDays, today],
  );

  const readFailed = dates.error !== null || settings.error !== null;
  if (!readFailed && !module.failed && !wanted) return null;
  const loading = !readFailed && !module.failed && (dates.loading || settings.loading || module.loading);

  const calendar = module.calendar;
  return (
    <Card
      title={t('importantDates.upcoming.title')}
      loading={loading}
      error={readFailed || module.failed}
      errorText={t('importantDates.upcoming.loadError')}
      onRetry={() => {
        dates.retry();
        settings.retry();
        module.retry();
      }}
      testId="upcoming-card"
    >
      {entries.length === 0 ? (
        <p className={styles.dates} data-testid="upcoming-empty">
          {t('importantDates.upcoming.empty')}
        </p>
      ) : null}
      <ul className={styles.entries}>
        {entries.map((entry) => {
          const lunar =
            calendar !== null &&
            (entry.kind === 'date'
              ? entry.fields.date.calendar === CalendarId.ChineseLunar
              : entry.packId.startsWith(PACK_LUNAR_PREFIX))
              ? lunarLabelOf(calendar, t, entry.date)
              : null;
          return (
            <li
              key={`${entry.kind}:${entry.id}`}
              className={styles.entry}
              data-testid="upcoming-entry"
              data-kind={entry.kind}
              data-days-until={entry.daysUntil}
            >
              <span
                className={cx(
                  styles.badge,
                  entry.daysUntil === 0 && styles.badgeToday,
                  entry.kind === 'holiday' && entry.daysUntil !== 0 && styles.badgeHoliday,
                )}
                data-testid="upcoming-countdown"
              >
                {countdownLabel(t, entry.daysUntil)}
              </span>
              <span className={styles.main}>
                <span
                  className={cx(styles.title, entry.kind === 'holiday' && styles.holidayTitle)}
                  data-testid="upcoming-title"
                >
                  {entry.kind === 'date'
                    ? entry.fields.title
                    : t(`calendar.holiday.${entry.holidayId}`, { defaultValue: entry.holidayId })}
                </span>
                <span className={styles.dates} data-testid="upcoming-dates">
                  {' '}
                  {[entry.date, lunar].filter((part) => part !== null).join(' · ')}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
